'use client'

import { useState } from 'react'
import Link from '@/modules/shared/components/Link'
import clsx from 'clsx'
import { Eye } from 'lucide-react'
import { HsTreeResponseDto, HsCodeDto } from '@/modules/hs/types/hs-response'

interface HsTreeToggleProps {
  tree: HsTreeResponseDto
  hsCode: string
}

export default function HsTreeToggle({ tree, hsCode }: HsTreeToggleProps) {
  const [isOpen, setIsOpen] = useState(false)

  const chapter = tree.chapters[0]

  return (
    <div>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex w-full cursor-pointer items-center gap-3 rounded-lg border-2 p-3.5 transition-all',
          isOpen
            ? 'border-[#1E40AF] bg-[#EFF6FF]'
            : 'border-[#e5e5e5] bg-[#fafafa] hover:border-[#1E40AF] hover:bg-[#EFF6FF]'
        )}
      >
        <div className="flex size-[30px] items-center justify-center rounded-lg bg-[#1E40AF] text-white">
          <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
            />
          </svg>
        </div>
        <div className="flex-1 text-left">
          <div className="text-[15px] font-bold leading-snug">
            Mô tả chi tiết mã HS
          </div>
        </div>
        <svg
          className={clsx(
            'size-5 text-[#a3a3a3] transition-transform duration-300',
            isOpen && 'rotate-180'
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Tree Content */}
      <div
        className={clsx(
          'overflow-hidden transition-all duration-300',
          isOpen ? 'mt-3 max-h-[1000px]' : 'max-h-0'
        )}
      >
        <div className="rounded-xl bg-[#fafafa] p-4">
          {/* Part */}
          <TreeLevel>
            <div className="text-sm font-semibold leading-relaxed">
              <span className="inline-block rounded bg-[#e5e5e5] px-1.5 py-0.5">
                {tree.numberVN}
              </span>
              {" - "}{tree.nameVN}
            </div>
          </TreeLevel>

          {/* Chapter */}
          <TreeLevel>
            <div className="text-sm font-semibold leading-relaxed">
              <span className="inline-block rounded bg-[#e5e5e5] px-1.5 py-0.5">
                {chapter?.numberVN}
              </span>
              {" - "}{chapter?.nameVN}
            </div>
          </TreeLevel>

          {/* HS Code Tree */}
          {chapter?.hsCodes && (
            <HsCodeTreeDisplay hsCodes={chapter.hsCodes} currentCode={hsCode} />
          )}
        </div>
      </div>
    </div>
  )
}

function TreeLevel({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b border-[#e5e5e5] py-3 last:border-b-0">
      {children}
    </div>
  )
}

function HsCodeTreeDisplay({
  hsCodes,
  currentCode,
  level = 0,
}: {
  hsCodes: HsCodeDto[]
  currentCode: string
  level?: number
}) {
  return (
    <>
      {hsCodes.map((code) => {
        const hasChildren = code.children && code.children.length > 0
        const isSiblingLevel =
          hasChildren && code.children.some((c) => c.hsCode === currentCode)

        // Calculate indentation based on level
        const paddingLeft = level > 0 ? `${level * 16}px` : '0px'

        return (
          <div key={code.hsId}>
            {/* Regular tree level */}
            {!isSiblingLevel && (
              <TreeLevel>
                <div
                  className='text-sm'
                  style={{ paddingLeft }}
                >
                  <span className="inline-block rounded bg-[#e5e5e5] px-1.5 py-0.5 mr-2 font-semibold ">
                    {code.hsCode}
                  </span>
                  {code.descriptionVN}
                </div>
              </TreeLevel>
            )}

            {/* Sibling display when we're at the parent of current code */}
            {isSiblingLevel && (
              <TreeLevel>
                <div
                  className="text-sm leading-relaxed"
                  style={{ paddingLeft }}
                >
                  <span className="inline-block rounded bg-[#e5e5e5] px-1.5 py-0.5 mr-2  font-semibold">
                    {code.hsCode}
                  </span>
                  {code.descriptionVN}
                </div>
                <div
                  className="border-t border-dashed border-[#e5e5e5] pt-3"
                  style={{ paddingLeft: `${(level + 1) * 16}px` }}
                >
                  {code.children.map((sibling) => {
                    const isDetailCode = sibling.isDetail ?? (!sibling.children?.length && sibling.hsCode && sibling.hsCode.length >= 8);

                    if (isDetailCode) {
                      return (
                        <SiblingItem
                          key={sibling.hsId}
                          code={sibling.hsCode}
                          description={sibling.descriptionVN}
                          isCurrent={sibling.hsCode === currentCode}
                        />
                      )
                    }

                    return (
                      <div key={sibling.hsId} className="text-sm leading-relaxed mb-2 px-2 text-[#525252]">
                        {sibling.hsCode && (
                          <span className="inline-block rounded bg-[#e5e5e5] px-1.5 py-0.5 mr-2 font-semibold">
                            {sibling.hsCode}
                          </span>
                        )}
                        <span>{sibling.descriptionVN}</span>
                      </div>
                    )
                  })}
                </div>
              </TreeLevel>
            )}

            {/* Recurse for non-sibling children */}
            {hasChildren && !isSiblingLevel && (
              <HsCodeTreeDisplay
                hsCodes={code.children}
                currentCode={currentCode}
                level={level + 1}
              />
            )}
          </div>
        )
      })}
    </>
  )
}

function SiblingItem({
  code,
  description,
  isCurrent,
}: {
  code: string
  description: string
  isCurrent: boolean
}) {
  const content = (
    <div
      className={clsx(
        'flex items-center gap-2 py-1 text-md px-2 rounded transition-colors font-semibold',
        isCurrent
          ? ' text-[#1E40AF] bg-[#EFF6FF]'
          : 'text-[#525252] hover:bg-[#f5f5f5]'
      )}
    >
      <span
        className={clsx(
          'min-w-[80px] font-semibold px-2 py-0.5 rounded',
          isCurrent && 'bg-[#1E40AF] text-white'
        )}
      >
        {code}
      </span>
      <span className="flex-1">{description}</span>

      {isCurrent && (
        <span className="flex items-center gap-1.5 text-xs font-semibold text-[#1E40AF] bg-white px-2 py-1 rounded border border-[#1E40AF]/20 ml-auto shrink-0">
          <Eye size={14} />
          Bạn đang xem
        </span>
      )}
    </div>
  )

  if (isCurrent) {
    return content
  }

  return (
    <Link href={`/chi-tiet-ma-hs/${code}`} className="block">
      {content}
    </Link>
  )
}
